/**
 * @fileoverview ClusterDetailPanel — expanded child panel for a single cluster
 * group in the Master/Detail view.
 *
 * Groups the cluster's records by `placeId`. Each sub-group shows:
 *  - A **checkbox** on the sub-group header that selects/deselects every
 *    individual mapping-row checkbox below it (select all / deselect all)
 *  - A clickable section box that opens {@link UnifiedNameDrawer} on click
 *  - Market rows: Market, Venue ID, Venue Name, City, Merged — each with its
 *    own row-level checkbox (rows with `wasMerged` remain selectable)
 *
 * The final **Merged** column marks individual mapping rows that were
 * re-linked from a deprecated source master venue into this cluster.
 * Matching is performed by comparing the row's `marketId`, `marketType`,
 * and `sourceVenueId` against the `mergedMarkets` arrays in
 * {@link ClusterDetailPanelProps.mergeHistory}.
 *
 * Selection state lives entirely in `checkedRowIds` (individual mapping-row
 * IDs), owned by `MasterGroupedView` and passed down. There is no local
 * per-sub-group selection state here: a sub-group's header checkbox is purely
 * **derived** — checked only when every one of its rows is present in
 * `checkedRowIds`, indeterminate when some (but not all) are. The header
 * checkbox toggles the underlying rows via `onToggleRow`, it does not
 * maintain any selection state of its own.
 *
 * A merge is only ever possible between records sharing the same
 * `ClusterCode` (with different `PlaceId`s). When `selectionDisabled` is
 * `true` — meaning the current merge selection belongs to a *different*
 * `ClusterCode` group than this panel's — every checkbox here is disabled so
 * the constraint is visible up front rather than only rejected on click.
 */

import * as React from 'react';
import {
    Box,
    Checkbox,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import type { MasterVenueRecord } from '../utils';
import type { ClusterMergeHistoryEntry } from './MasterGroupedView';
import MarketLabel from './MarketLabel';

// ---------------------------------------------------------------------------
// Column width constants (child mapping table)
// ---------------------------------------------------------------------------

/**
 * Percentage widths for the child mapping table columns within each
 * sub-group. The first column holds the row-level checkbox.
 * Order: checkbox, market, venueId, venueName, city, merged.
 */
const CHILD_COL_WIDTHS = {
    checkbox: '4%',
    market:  '16%',
    venueId: '12%',
    venue:   '34%',
    city:    '24%',
    merged:  '10%',
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A sub-group of {@link MasterVenueRecord}s sharing the same `placeId`
 * within a ClusterCode group.
 */
export interface PlaceIdSubGroup {
    /** Google Places unique identifier; empty string for records without one. */
    placeId: string;
    /** The `MasterVenueId` associated with this sub-group (from the first record). */
    masterVenueId: number;
    /** Display name for this location. */
    googleUnifiedName: string;
    /** Formatted address. */
    formattedAddress: string;
    /** Google Maps URI. */
    googleMapsUri: string;
    /** All mapping records that belong to this sub-group. */
    records: MasterVenueRecord[];
}

// ---------------------------------------------------------------------------
// Sub-group builder
// ---------------------------------------------------------------------------

/**
 * Groups records within a ClusterCode group by their `placeId`.
 *
 * @param records - All records belonging to one ClusterCode group.
 * @returns Ordered array of {@link PlaceIdSubGroup} objects.
 */
export function buildPlaceIdSubGroups (records: MasterVenueRecord[]): PlaceIdSubGroup[] {
    const map = new Map<string, MasterVenueRecord[]>();
    records.forEach(r => {
        const key = r.placeId || '';
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(r);
    });

    return Array.from(map.entries()).map(([placeId, recs]) => {
        const sample = recs[0];
        return {
            placeId,
            masterVenueId:     sample.masterVenueId,
            googleUnifiedName: sample.googleUnifiedName || '',
            formattedAddress:  sample.formattedAddress  || '',
            googleMapsUri:     sample.googleMapsUri      || '',
            records: recs,
        };
    });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Builds a `Set` of lookup keys from the merge-history `mergedMarkets` arrays.
 * Each key has the form `{marketId}|{marketType}|{sourceVenueId}`.
 *
 * @param mergeHistory - Merge history entries for the cluster.
 * @returns Set of lookup keys for O(1) row matching.
 */
function buildMergedMarketKeySet (mergeHistory: ClusterMergeHistoryEntry[]): Set<string> {
    const keys = new Set<string>();
    mergeHistory.forEach(entry => {
        entry.mergedMarkets.forEach(m => {
            keys.add(`${m.marketId}|${m.marketType}|${m.sourceVenueId}`);
        });
    });
    return keys;
}

// ---------------------------------------------------------------------------
// SubGroupCard — one PlaceId sub-group with header + row checkboxes
// ---------------------------------------------------------------------------

/** Props for {@link SubGroupCard}. */
interface SubGroupCardProps {
    /** The sub-group to render. */
    sg: PlaceIdSubGroup;
    /** Sub-group identity key (`placeId` or `__mv_{masterVenueId}`), used for ARIA labelling only. */
    checkKey: string;
    /** Set of currently-checked mapping-row IDs, owned by `MasterGroupedView`. */
    checkedRowIds: Set<string>;
    /** Toggles a single mapping row's checked state in the parent. */
    onToggleRow: (id: string) => void;
    /** Opens the map-preview drawer for this sub-group. */
    onOpenDrawer: (unifiedName: string, formattedAddress: string, googleMapsUri: string) => void;
    /** O(1) lookup set of rows re-linked during a merge (for the Merged chip). */
    mergedKeySet: Set<string>;
    /** Whether the current theme is dark. */
    isDark: boolean;
    /** Disables every checkbox in this sub-group — see {@link ClusterDetailPanelProps.selectionDisabled}. */
    selectionDisabled: boolean;
}

/**
 * Renders one PlaceId sub-group: a header (checkbox + clickable venue info)
 * and a table of its mapping rows, each with its own checkbox.
 *
 * All selection state is derived from `checkedRowIds` — there is no local
 * selection state here. The header checkbox reflects the aggregate state of
 * its rows (checked only when every row ID is in `checkedRowIds`,
 * indeterminate when some but not all are) and, when clicked, toggles the
 * rows that need to change to reach the "all selected"/"all deselected"
 * state via `onToggleRow`.
 */
const SubGroupCard: React.FC<SubGroupCardProps> = ({
    sg,
    checkKey,
    checkedRowIds,
    onToggleRow,
    onOpenDrawer,
    mergedKeySet,
    isDark,
    selectionDisabled,
}) => {
    const rowIds = React.useMemo(() => sg.records.map(r => r.id), [sg.records]);
    const checkedCount = rowIds.filter(id => checkedRowIds.has(id)).length;
    const allRowsChecked = rowIds.length > 0 && checkedCount === rowIds.length;
    const someRowsChecked = checkedCount > 0 && !allRowsChecked;

    /** Selects/deselects all rows in this sub-group (the header checkbox). */
    const handleToggleAll = (): void => {
        if (allRowsChecked) {
            // Deselect every currently-checked row.
            rowIds.forEach(id => onToggleRow(id));
        } else {
            // Select only the rows that aren't already checked.
            rowIds.filter(id => !checkedRowIds.has(id)).forEach(id => onToggleRow(id));
        }
    };

    return (
        <Box
            sx={{
                mb: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1
            }}
        >
            {/* Sub-group header with checkbox and unified name */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0.5,
                borderColor: allRowsChecked ? 'primary.main' : 'divider',
                bgcolor: allRowsChecked
                    ? isDark ? 'primary.dark' : 'primary.light'
                    : undefined,
            }}>
                <Checkbox
                    size="small"
                    checked={allRowsChecked}
                    indeterminate={someRowsChecked}
                    onChange={handleToggleAll}
                    disabled={selectionDisabled}
                    inputProps={{ 'aria-label': `Select sub-group ${sg.googleUnifiedName || checkKey} for merge` }}
                />

                {/* Clickable venue info box — opens UnifiedNameDrawer */}
                <Box
                    sx={{
                        minWidth: 0,
                        cursor: sg.googleUnifiedName ? 'pointer' : 'default',
                        '&:hover .venue-name': sg.googleUnifiedName
                            ? { textDecoration: 'underline', color: 'primary.dark' }
                            : undefined,
                    }}
                    onClick={() => {
                        if (sg.googleUnifiedName) {
                            onOpenDrawer(
                                sg.googleUnifiedName,
                                sg.formattedAddress,
                                sg.googleMapsUri,
                            );
                        }
                    }}
                    aria-label={
                        sg.googleUnifiedName
                            ? `Open map preview for ${sg.googleUnifiedName}`
                            : undefined
                    }
                    role={sg.googleUnifiedName ? 'button' : undefined}
                >
                    {sg.googleUnifiedName && (
                        <Typography
                            className="venue-name"
                            variant="body2"
                            fontWeight={500}
                            sx={{ color: 'primary.main', transition: 'color 0.15s ease' }}
                        >
                            {sg.googleUnifiedName}
                        </Typography>
                    )}
                    {sg.formattedAddress && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                            <LocationOnIcon fontSize="inherit" />
                            {sg.formattedAddress}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Mappings in this sub-group */}
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: CHILD_COL_WIDTHS.checkbox, p: 0 }} />
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.market }}>Market</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.venueId }}>Venue ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.venue }}>Venue Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.city }}>City</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.merged }}>Merged</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sg.records.map(record => {
                        const mergeKey = `${record.marketId}|${record.marketType}|${record.sourceVenueId}`;
                        const wasMerged = mergedKeySet.has(mergeKey);
                        const rowChecked = checkedRowIds.has(record.id);

                        return (
                            <TableRow
                                key={record.id}
                                sx={wasMerged ? {
                                    bgcolor: isDark
                                        ? 'rgba(255, 167, 38, 0.08)'
                                        : 'rgba(255, 167, 38, 0.06)',
                                } : undefined}
                            >
                                <TableCell sx={{ p: 0 }}>
                                    <Checkbox
                                        size="small"
                                        checked={rowChecked}
                                        onChange={() => onToggleRow(record.id)}
                                        disabled={selectionDisabled}
                                        inputProps={{ 'aria-label': `Select mapping ${record.marketVenueName || record.id} for merge` }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <MarketLabel
                                        marketId={record.marketId}
                                        marketType={record.marketType}
                                    />
                                </TableCell>
                                <TableCell>
                                    {record.sourceVenueId ?? '—'}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" component="span" display="block" noWrap>
                                        {record.marketVenueName || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>
                                    {record.marketVenueCity || '—'}
                                </TableCell>
                                <TableCell>
                                    {wasMerged ? (
                                        <Tooltip
                                            title="This mapping was re-linked from a merged venue"
                                            placement="top"
                                        >
                                            <Chip
                                                label="Merged"
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                                sx={{ fontSize: '0.62rem', height: 18 }}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link ClusterDetailPanel}. */
export interface ClusterDetailPanelProps {
    /** All records in this cluster group. */
    records: MasterVenueRecord[];
    /** Set of currently-checked mapping-row IDs (source of truth for all selection state). */
    checkedRowIds: Set<string>;
    /** Called when a single mapping row's checkbox is toggled. */
    onToggleRow: (id: string) => void;
    /**
     * Called when the sub-group name/address section is clicked to open the
     * map drawer in the parent component.
     */
    onOpenDrawer: (unifiedName: string, formattedAddress: string, googleMapsUri: string) => void;
    /**
     * Merge history entries for this cluster, used to mark individual rows
     * that were re-linked from a deprecated source master venue.
     */
    mergeHistory: ClusterMergeHistoryEntry[];
    /**
     * `true` when the current merge selection belongs to a *different*
     * `ClusterCode` group than this panel's — disables every checkbox here
     * since a merge can only ever happen within a single `ClusterCode`.
     * Defaults to `false`.
     */
    selectionDisabled?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Expanded panel for a ClusterCode group in the Master/Detail view.
 *
 * Groups records by PlaceId. Each sub-group ({@link SubGroupCard}) has:
 * - One **header checkbox** that selects/deselects all of its row checkboxes
 * - A clickable venue-info box that opens {@link UnifiedNameDrawer} via `onOpenDrawer`
 * - A table of market venue mappings (Market, Venue ID, Venue Name, City, Merged),
 *   each row individually selectable — rows that `wasMerged` remain selectable.
 *
 * Individual rows whose `marketId`+`marketType`+`sourceVenueId` appear in
 * {@link ClusterDetailPanelProps.mergeHistory} `mergedMarkets` are highlighted
 * with a **Merged** chip to indicate they were re-linked from a merged-away source.
 *
 * @param props.records           - Records in the cluster.
 * @param props.checkedRowIds     - Currently-checked mapping-row IDs.
 * @param props.onToggleRow       - Toggle callback for a single mapping row's checkbox.
 * @param props.onOpenDrawer      - Opens the map drawer for the clicked sub-group.
 * @param props.mergeHistory      - Merge history for this cluster.
 * @param props.selectionDisabled - Disables all checkboxes when the active merge selection is in another cluster.
 */
const ClusterDetailPanel: React.FC<ClusterDetailPanelProps> = ({
    records,
    checkedRowIds,
    onToggleRow,
    onOpenDrawer,
    mergeHistory,
    selectionDisabled = false,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const subGroups = React.useMemo(() => buildPlaceIdSubGroups(records), [records]);

    /** O(1) lookup set for rows that were re-linked during a merge. */
    const mergedKeySet = React.useMemo(
        () => buildMergedMarketKeySet(mergeHistory),
        [mergeHistory],
    );

    return (
        <Box
            sx={{
                py: 1,
                bgcolor: isDark ? 'grey.900' : 'grey.50',
                '& .MuiTableCell-root': {
                    borderColor: isDark ? 'grey.700' : undefined,
                },
            }}
        >
            {subGroups.map(sg => {
                const checkKey = sg.placeId || `__mv_${sg.masterVenueId}`;

                return (
                    <SubGroupCard
                        key={checkKey}
                        sg={sg}
                        checkKey={checkKey}
                        checkedRowIds={checkedRowIds}
                        onToggleRow={onToggleRow}
                        onOpenDrawer={onOpenDrawer}
                        mergedKeySet={mergedKeySet}
                        isDark={isDark}
                        selectionDisabled={selectionDisabled}
                    />
                );
            })}
        </Box>
    );
};

export default ClusterDetailPanel;
